import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import MangaCard from "@/components/common/MangaCard";
import JsonLd from "@/components/common/JsonLd";
import useSeo from "@/hooks/useSeo";
import { categories, mangas, getMangaByCategory, type Manga } from "@/mocks/manga";

const tabs = [
  { label: "人气榜", slug: "hot" },
  { label: "完结榜", slug: "completed" },
  { label: "新作", slug: "new" },
  { label: "热血", slug: "rexue" },
  { label: "恋爱", slug: "lianai" },
  { label: "奇幻", slug: "qihuan" },
  { label: "悬疑", slug: "xuanyi" },
  { label: "搞笑", slug: "gaoxiao" },
  { label: "科幻", slug: "kehuan" },
  { label: "冒险", slug: "maoxian" },
  { label: "恐怖", slug: "kongbu" },
  { label: "日常", slug: "richang" },
  { label: "运动", slug: "yundong" },
];

type SortKey = "views" | "rating" | "update";

export default function CategoryPage() {
  const { slug = "hot" } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [sort, setSort] = useState<SortKey>("views");

  const { title, subtitle, list } = useMemo(() => {
    let items: Manga[] = [];
    let t = "人气榜";
    let sub = "最受读者欢迎的漫画作品";

    if (slug === "search") {
      const kw = q.trim().toLowerCase();
      items = kw
        ? mangas.filter(
            (m) =>
              m.title.toLowerCase().includes(kw) ||
              m.categories.some((c) => c.toLowerCase().includes(kw)),
          )
        : [];
      t = "搜索结果";
      sub = kw ? `关键词“${q}”共 ${items.length} 部相关作品` : "请输入关键词进行搜索";
    } else if (slug === "hot") {
      items = [...mangas].sort((a, b) => b.views - a.views);
      t = "人气榜";
      sub = "最受读者欢迎的漫画作品";
    } else if (slug === "completed") {
      items = mangas.filter((m) => m.status === "完结");
      t = "完结榜";
      sub = "已完整收录、可一口气看完的佳作";
    } else if (slug === "new") {
      items = mangas.filter((m) => m.isNew);
      t = "新作";
      sub = "最新上架、抢先追更的漫画";
    } else {
      const cat = categories.find((c) => c.slug === slug);
      items = cat ? getMangaByCategory(slug) : [];
      t = cat ? `${cat.name}漫画` : "漫画分类";
      sub = cat ? cat.desc : "该分类暂无作品";
    }

    if (sort === "views") items = [...items].sort((a, b) => b.views - a.views);
    else if (sort === "rating") items = [...items].sort((a, b) => b.rating - a.rating);
    else items = [...items].sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1));

    return { title: t, subtitle: sub, list: items };
  }, [slug, q, sort]);

  useSeo({
    title: slug === "search" ? "搜索结果" : title,
    description: `${title}：${subtitle}，浏览${title}下的热门漫画作品，随时免费在线阅读。`,
    keywords: `91漫画,${title},91漫画人气榜,91漫画排行`,
    path: `/category/${slug}`,
  });

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://zhuanpan91.github.io/" },
            { "@type": "ListItem", position: 2, name: title, item: `https://zhuanpan91.github.io/category/${slug}` },
          ],
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: title,
          url: `https://zhuanpan91.github.io/category/${slug}`,
          description: subtitle,
          inLanguage: "zh-CN",
        }}
      />

      {/* page header */}
      <section className="pt-24 pb-8 bg-gradient-to-b from-primary-100/70 to-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-7xl">
          <nav aria-label="面包屑导航" className="text-xs text-foreground-500 mb-4">
            <ol className="flex items-center gap-1 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li className="text-foreground-400">/</li>
              <li aria-current="page" className="text-foreground-700">{title}</li>
            </ol>
          </nav>
          <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">{title}</h1>
          <p className="mt-2 text-sm text-foreground-600">{subtitle}</p>
        </div>
      </section>

      {/* tabs + sort */}
      <section className="py-6 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-wrap gap-2 mb-5">
            {tabs.map((tb) => {
              const active = slug === tb.slug;
              return (
                <Link
                  key={tb.slug}
                  to={`/category/${tb.slug}`}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${
                    active
                      ? "bg-primary-500 border-primary-500 text-background-50"
                      : "bg-background-100 border-background-200/70 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {tb.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <p className="text-sm text-foreground-600">共 {list.length} 部作品</p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
              {(["views", "rating", "update"] as SortKey[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSort(k)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap cursor-pointer transition-colors ${
                    sort === k ? "bg-background-50 text-primary-600 font-medium" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {k === "views" ? "按人气" : k === "rating" ? "按评分" : "按更新"}
                </button>
              ))}
            </div>
          </div>

          {list.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop="manga-list">
              {list.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <i className="ri-search-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-sm text-foreground-600">没有找到相关作品，换个分类或关键词试试吧</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}